import React, { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from '@xstate/react';
import { Alert, Box } from '@chargify/custom-ui-components';
import { useToastMessages } from '@chargify/toasts';
import {
  ComponentsAllocationEdit,
  ComponentsAllocationPreview,
} from '../../components';

import { addConfigurationSelectors } from '../../machines/addOnsConfiguration';
import {
  getErrorDescription,
  mergeComponentsWithAllocations,
} from '../../utils';
import { GlobalStateContext } from '../../contexts';
import { AddonsConfigurationService } from '../../hooks/useAddonsConfigurationService';

export type Props = {
  addOnsConfigurationService: AddonsConfigurationService;
  subscriptionId: number;
  onClose: () => void;
};

const ComponentsAllocationContainer = ({
  addOnsConfigurationService,
  subscriptionId,
  onClose,
}: Props) => {
  const { t } = useTranslation(['subscriptions-manager']);
  const { subscriptionsService } = useContext(GlobalStateContext);
  const toastService = useToastMessages();

  const {
    configureAllocationsStep,
    previewAllocationsStep,
    configureAllocationsStepLoading,
    updateSuccess,
  } = useSelector(
    addOnsConfigurationService,
    addConfigurationSelectors.getAddonsState
  );

  const { components } = useSelector(
    addOnsConfigurationService,
    addConfigurationSelectors.getAddonsComponents
  );

  const allocations = useSelector(
    addOnsConfigurationService,
    addConfigurationSelectors.getAllocations
  );
  const mergedComponents = mergeComponentsWithAllocations(
    components,
    allocations
  );

  const { error } = useSelector(
    addOnsConfigurationService,
    addConfigurationSelectors.getAllocationError
  );

  const { allocationsPreview } = useSelector(
    addOnsConfigurationService,
    addConfigurationSelectors.getAllocationsPreview
  );

  useEffect(() => {
    if (updateSuccess) {
      subscriptionsService.send({
        type: 'UPDATE_SUBSCRIPTION_COMPONENTS',
        subscriptionId,
      });
      onClose();

      toastService.show({
        level: 'success',
        message: t('changes_saved', {
          ns: 'common',
        }),
      });
    }
  }, [updateSuccess]);

  return (
    <>
      {error && (
        <Box marginBottom={6}>
          <Alert color="danger">
            <Alert.TextMessage>
              {getErrorDescription(error, t)}
            </Alert.TextMessage>
          </Alert>
        </Box>
      )}
      {configureAllocationsStepLoading && (
        <ComponentsAllocationEdit.LoadingSkeleton />
      )}
      {configureAllocationsStep && (
        <ComponentsAllocationEdit
          components={mergedComponents}
          errors={error?.errors}
          onUpdateAllocation={(allocation) =>
            addOnsConfigurationService.send('UPDATE_ALLOCATIONS_LIST', {
              allocation,
            })
          }
        />
      )}
      {previewAllocationsStep && allocationsPreview && (
        <ComponentsAllocationPreview
          components={components}
          allocationsPreview={allocationsPreview}
        />
      )}
    </>
  );
};

export default ComponentsAllocationContainer;
